// src/pages/driver-management/DetailDriverScorePeriodicModal.jsx
'use client';

import { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Stack,
  Divider
} from '@mui/material';
import dayjs from 'dayjs';

import DetailDialog from '../../components/DetailDialog';
import { useLanguage } from '../../utils/i18n';
import { getDriverScorePeriodicById } from '../../stores/driverScorePeriodicStore';
import { getDriverScoreConfigById } from '../../stores/driverScoreConfigStore';
import { SOURCE_GROUPS, EVENT_TYPE_OPTIONS, getSourceGroupLabel, getEventTypeLabel } from './AddScoreConfigModal';

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const d = dayjs(dateStr);
  return d.isValid() ? d.format('YYYY-MM-DD HH:mm') : dateStr;
};

export default function DetailDriverScorePeriodicModal({ open, periodicId, initialRow, onClose }) {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [periodicData, setPeriodicData] = useState(null);
  const [configData, setConfigData] = useState(null);

  useEffect(() => {
    if (!open) {
      setPeriodicData(null);
      setConfigData(null);
      return;
    }

    let isMounted = true;
    const loadDetail = async () => {
      setLoading(true);
      try {
        let pData = initialRow || null;
        if (periodicId) {
          try {
            const res = await getDriverScorePeriodicById(periodicId);
            if (res) pData = res;
          } catch (e) {
            console.error('Failed to get periodic detail by id', e);
          }
        }
        if (!isMounted) return;
        setPeriodicData(pData);

        const configId = pData?.score_config ?? pData?.score_config_id;
        if (configId) {
          try {
            const cRes = await getDriverScoreConfigById(configId);
            if (isMounted) setConfigData(cRes);
          } catch (e) {
            console.error('Failed to get score config detail', e);
          }
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadDetail();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, periodicId]); // Removed initialRow to prevent infinite update loop

  const parsedParameters = useMemo(() => {
    const res = {};
    EVENT_TYPE_OPTIONS.forEach((opt) => {
      res[opt.value] = 0;
    });

    if (!configData) return res;

    if (configData.events && typeof configData.events === 'object' && !Array.isArray(configData.events)) {
      Object.keys(configData.events).forEach((k) => {
        const normalizedKey = k.replace(/-/g, '_').toLowerCase();
        if (res[normalizedKey] !== undefined) {
          res[normalizedKey] = Number(configData.events[k] ?? 0);
        } else if (res[k] !== undefined) {
          res[k] = Number(configData.events[k] ?? 0);
        }
      });
    } else if (Array.isArray(configData.parameters)) {
      configData.parameters.forEach((p) => {
        const rawKey = p.parameter_name || p.key || p.name || '';
        const key = rawKey.replace(/-/g, '_').toLowerCase();
        if (key && res[key] !== undefined) {
          res[key] = Number(p.value ?? 0);
        }
      });
    } else if (configData.parameters && typeof configData.parameters === 'object') {
      Object.keys(configData.parameters).forEach((k) => {
        const normalizedKey = k.replace(/-/g, '_').toLowerCase();
        if (res[normalizedKey] !== undefined) {
          res[normalizedKey] = Number(configData.parameters[k] ?? 0);
        }
      });
    }

    return res;
  }, [configData]);

  const scoreIndexes = Array.isArray(configData?.score_indexes) ? configData.score_indexes : [];

  return (
    <DetailDialog
      open={open}
      onClose={onClose}
      title={t('driverScore.driverScorePeriodic', 'Driver Score Periodic') + t('detail', 'Detail')}
      subtitle={t('driverScore.periodicDetailSubtitle', 'Detail periodic evaluation and driver score configuration setting')}
      maxWidth="md"
    >
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Section 1: Periodic Evaluation Info */}
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, bgcolor: 'background.paper' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5, color: 'primary.main' }}>
              {t('driverScore.evaluationInformation', 'Evaluation Information')}
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="caption" color="text.secondary">
                  {t('masterDriver.driver', 'Driver')}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {periodicData?.driver_name || '-'}
                  {periodicData?.driver_code ? ` (${periodicData.driver_code})` : ''}
                </Typography>
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="caption" color="text.secondary">
                  {t('general.company', 'Company')}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {periodicData?.company_name || '-'}
                </Typography>
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="caption" color="text.secondary">
                  {t('driverScore.startTime', 'Start Time')}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {formatDate(periodicData?.start_time)}
                </Typography>
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="caption" color="text.secondary">
                  {t('driverScore.endTime', 'End Time')}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {formatDate(periodicData?.end_time)}
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* Section 2: Score Config Overview */}
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, bgcolor: 'background.paper' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5, color: 'primary.main' }}>
              {t('driverScore.scoreConfig', 'Score Config')} Setting
            </Typography>

            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Typography variant="caption" color="text.secondary">
                  {t('general.name', 'Configuration Name')}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {configData?.name || periodicData?.score_config_name || '-'}
                </Typography>
              </Grid>

              <Grid size={{ xs: 12, sm: 4 }}>
                <Typography variant="caption" color="text.secondary">
                  {t('driverScore.minDistance', 'Minimum Distance')}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {configData?.min_dist_km != null ? `${configData.min_dist_km} km` : '-'}
                </Typography>
              </Grid>

              <Grid size={{ xs: 12, sm: 4 }}>
                <Typography variant="caption" color="text.secondary">
                  {t('driverScore.warningThreshold', 'Warning Threshold')}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {configData?.pct_warn_limit != null ? `${Number(configData.pct_warn_limit).toFixed(2)}%` : '-'}
                </Typography>
              </Grid>
            </Grid>

            {/* Event Weighting Parameters */}
            {configData && Object.keys(parsedParameters).length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
                  {t('driverScore.eventWeightings', 'Event Weightings')}
                </Typography>

                <Stack spacing={2}>
                  {SOURCE_GROUPS.map((group) => {
                    const groupEvents = EVENT_TYPE_OPTIONS.filter((e) => e.source === group.value);
                    if (groupEvents.length === 0) return null;

                    return (
                      <Box key={group.value} sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1.5 }}>
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 700, textTransform: 'uppercase', color: `${group.color}.main`, mb: 1, display: 'block' }}
                        >
                          {getSourceGroupLabel(t, group.value)}
                        </Typography>
                        <Grid container spacing={1.5}>
                          {groupEvents.map((evt) => {
                            const val = parsedParameters[evt.value] ?? 0;
                            return (
                              <Grid size={{ xs: 6, sm: group.gridMd || 3 }} key={evt.value}>
                                <Paper variant="outlined" sx={{ p: 1, textAlign: 'center', bgcolor: 'background.paper' }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', noWrap: true }}>
                                    {getEventTypeLabel(t, evt)}
                                  </Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 700, color: `${evt.color}.main` }}>
                                    {val}%
                                  </Typography>
                                </Paper>
                              </Grid>
                            );
                          })}
                        </Grid>
                      </Box>
                    );
                  })}
                </Stack>
              </>
            )}

            {/* Score Indexes Table */}
            {scoreIndexes.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
                  {t('driverScore.scoreGradeIndexes', 'Score Grade Indexes')}
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1.5 }}>
                  <Table size="small">
                    <TableHead sx={{ bgcolor: 'action.hover' }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>{t('driverScore.grade', 'Grade')}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{t('driverScore.minScore', 'Min Score')}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{t('driverScore.maxScore', 'Max Score')}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{t('driverScore.minDistance', 'Min Distance')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {scoreIndexes.map((row, idx) => (
                        <TableRow key={idx}>
                          <TableCell>
                            <Chip label={row.idx_name} size="small" color="primary" variant="outlined" sx={{ fontWeight: 700 }} />
                          </TableCell>
                          <TableCell>{row.min_idx}</TableCell>
                          <TableCell>{row.max_idx}</TableCell>
                          <TableCell>{row.min_dist_km != null ? `${row.min_dist_km} km` : '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </Paper>
        </Box>
      )}
    </DetailDialog>
  );
}

DetailDriverScorePeriodicModal.propTypes = {
  open: PropTypes.bool.isRequired,
  periodicId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  initialRow: PropTypes.object,
  onClose: PropTypes.func.isRequired
};
